import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Swal from "sweetalert2";
import axios from 'axios';
import * as pdfjsLib from "pdfjs-dist/webpack";

export default function CreateBlog() {
  const [loader, setLoader] = useState(false);
  const [pdfText, setPdfText] = useState("");
  const [pdfPreview, setPdfPreview] = useState(null);
  const [blog, setBlog] = useState({});
  const [selectedFile, setSelectedFile] = useState(null);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const navigate = useNavigate();

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    const file = files[0];
    if (file) {
      setSelectedFile(URL.createObjectURL(file));
      setBlog({ ...blog, [name]: file });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setBlog({ ...blog, [name]: value });
  };

  const handleDescriptionChange = (e) => {
    const { value } = e.target;
    setBlog({ ...blog, description: value });
  };

  const extractTextFromPdf = async (file) => {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

    let textContent = "";
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const text = await page.getTextContent();
      textContent += text.items.map((item) => item.str).join(" ") + "\n\n";
    }
    return textContent;
  };

  const handlePdfUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.type !== "application/pdf") {
      Swal.fire("Please upload a valid PDF file!");
      return;
    }

    Swal.fire({
      title: "Extracting text...",
      text: "Please wait",
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });

    try {
      const extractedText = await extractTextFromPdf(file);

      let paragraphs = extractedText
        .split(/\n\s*\n/)
        .map(p => p.trim())
        .filter(p => p.length > 0);

      const description = paragraphs.slice(0, 2).join("\n\n");
      setPdfText(description);
      setBlog(prev => ({ ...prev, description, pdf: file }));
      setPdfPreview(file.name);
      setPdfPreviewUrl(URL.createObjectURL(file));

      Swal.fire("Text extracted successfully!", "", "success");
    } catch (error) {
      console.error(error);
      Swal.fire("Failed to extract PDF content", "", "error");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!blog.title || !blog.description) {
      return Swal.fire({ title: "Please fill all required fields", icon: "warning" });
    }

    setLoader(true);
    try {
      const formData = new FormData();
      Object.entries(blog).forEach(([key, value]) => {
        if (value) formData.append(key, value);
      });

      const res = await axios.post(`${process.env.REACT_APP_BACKEND_BASE_URL}/blog/store`, formData);

      if (res.status === 200 && res.data.data) {
        Swal.fire({ icon: "success", title: "Blog Created Successfully", timer: 1500 })
          .then(() => navigate("/bloglist"));
      } else {
        Swal.fire("Blog Insert Failed", "", "error");
      }
    } catch (err) {
      console.error(err);
      Swal.fire("Something went wrong!", err?.message, "error");
    } finally {
      setLoader(false);
    }
  };

  if (loader) {
    return (
      <div className="loader-container">
        <div className="loader"></div>
        <p>Processing, please wait...</p>
      </div>
    );
  }

  return (
    <div className="main-content">
      <div className="page-wrapper page-settings">
        <div className="content">
          {/* Breadcrumb and Header Section with Card Shadow */}
          <div
            className="mb-4"
            style={{
              background: "#ffffff",
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
              padding: "20px",
            }}
          >
              <div className="content-page-header content-page-headersplit">
              <h5>Create Blog</h5>
              <div className="list-btn">
                <Link to="/bloglist" className="btn btn-cancel">
                  <i className="fas fa-arrow-left me-2"></i>
                  Back
                </Link>
              </div>
            </div>
            <nav
              aria-label="breadcrumb"
              style={{ "--bs-breadcrumb-divider": "none", marginBottom: "15px" }}
            >
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="/dashboard">Dashboard</Link> /
                </li>
                <li className="breadcrumb-item">
                  <Link to="/bloglist">Blog</Link> /
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Create Blog
                </li>
              </ol>
            </nav>
          
          </div>

          {/* Form Section with Card Shadow */}
          <div
            style={{
              background: "#ffffff",
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
              padding: "20px",
            }}
          >
            <form onSubmit={handleSubmit}>
              <div className="row g-3">
                {/* Blog Title */}
                <div className="col-lg-6 col-md-6">
                  <div className="form-group">
                    <label style={{ marginBottom: "8px", fontWeight: 600, color: "#0D233E" }}>
                      Blog Title <span style={{ color: "#ef4444" }}>*</span>
                    </label>
                    <input
                      type="text"
                      name="title"
                      className="form-control"
                      placeholder="Enter blog title"
                      value={blog.title || ""}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>

                {/* Blog Image */}
                <div className="col-lg-6 col-md-6">
                  <div className="form-group">
                    <label style={{ marginBottom: "8px", fontWeight: 600, color: "#0D233E" }}>
                      Upload Image
                    </label>
                    <input
                      type="file"
                      name="photo"
                      accept="image/*"
                      className="form-control"
                      onChange={handleFileChange}
                    />
                    {selectedFile && (
                      <div className="mt-3">
                        <img
                          src={selectedFile}
                          alt="Selected"
                          style={{
                            width: "200px",
                            height: "200px",
                            objectFit: "cover",
                            borderRadius: "8px",
                            border: "1px solid #E3E3E3",
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Description */}
                <div className="col-12">
                  <div className="form-group">
                    <label style={{ marginBottom: "8px", fontWeight: 600, color: "#0D233E" }}>
                      Description <span style={{ color: "#ef4444" }}>*</span>
                    </label>
                    <textarea
                      name="description"
                      className="form-control"
                      rows="6"
                      placeholder="Enter blog description or upload PDF to extract text"
                      value={blog.description || ""}
                      onChange={handleDescriptionChange}
                      required
                    />
                    {pdfText && (
                      <small className="text-muted mt-2 d-block">
                        <i className="fas fa-info-circle me-1"></i>
                        Description extracted from PDF. You can edit it above.
                      </small>
                    )}
                  </div>
                </div>

                {/* PDF Upload */}
                <div className="col-12">
                  <div className="form-group">
                    <label style={{ marginBottom: "8px", fontWeight: 600, color: "#0D233E" }}>
                      Upload PDF (Optional)
                    </label>
                    <input
                      type="file"
                      name="pdf"
                      accept="application/pdf"
                      className="form-control"
                      onChange={handlePdfUpload}
                    />
                    {pdfPreviewUrl && (
                      <div className="mt-3">
                        <div
                          style={{
                            background: "#f9fafb",
                            borderRadius: "8px",
                            padding: "15px",
                            marginBottom: "15px",
                          }}
                        >
                          <p style={{ margin: 0, fontWeight: 600, color: "#006e82" }}>
                            <i className="fas fa-file-pdf me-2"></i>
                            PDF Preview: {pdfPreview}
                          </p>
                        </div>
                        <embed
                          src={pdfPreviewUrl}
                          type="application/pdf"
                          width="100%"
                          height="400px"
                          style={{ borderRadius: "8px", border: "1px solid #E3E3E3" }}
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Submit Button */}
                <div className="col-12">
                  <div className="btn-path">
                    <button type="submit" className="btn btn-primary">
                      <i className="fas fa-save me-2"></i>
                      Submit
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Loader Styles */}
      <style>{`
        .loader-container {
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          height: 70vh;
        }
        .loader {
          border: 8px solid #f3f3f3;
          border-top: 8px solid #006e82;
          border-radius: 50%;
          width: 70px;
          height: 70px;
          animation: spin 1s linear infinite;
          margin-bottom: 15px;
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
